import { isNullOrUndefined } from '@afterpay/utils'
import { useCallback, useEffect, useState } from 'react'
import { useCreateApplicantsMutation, useCreateInquiryIdMutation, useCheckBiometricStatusQuery, useGetIdentitiesQuery } from '../../state/identityBiometric'
import { Channel } from '@afterpaytouch/portal-api'
import {
  BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL,
  BiometricHardIdCheckHookStatus,
  PollingStatus,
  UseBiometricHardIdCheck,
  UseBiometricHardIdResponse,
  UseBiometricInquiryIdResponse,
} from '../../model/'
import { IdentityCheckStatus } from '@afterpay/types'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { showToast, useAppDispatch, useAppRouter, useBiometricOptOut } from '../../state'
import { Route } from '../../config/router'

export const useIdFlowErrorHandler = (): { handleError: () => void; showErrorToast: () => void } => {
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const showErrorToast = useCallback((): void => {
    logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: 'identityGenericError' })
    dispatch(showToast({ toastType: 'identityGenericError', toastKind: 'Failure' }))
  }, [dispatch, logEvent])

  const handleError = useCallback((): void => {
    showErrorToast()
    router.push(Route.HOME)
  }, [router, showErrorToast])

  return { showErrorToast, handleError }
}

export const useBiometricHardId = (): UseBiometricHardIdResponse => {
  const [
    createApplicants,
    { data: applicantsData, isSuccess: isCreateApplicantsSuccess, isError: isCreateApplicantError, isLoading: isCreateApplicantLoading },
  ] = useCreateApplicantsMutation()

  useEffect(() => {
    if (isCreateApplicantsSuccess || !isNullOrUndefined(applicantsData)) {
      return
    }

    createApplicants({ referrer: new URL(window.location.origin), channel: Channel.PORTAL })
  }, [createApplicants, applicantsData, isCreateApplicantsSuccess])

  return {
    sdkToken: applicantsData?.sdkToken,
    applicantId: applicantsData?.applicantId,
    isCreateApplicantError,
    isCreateApplicantLoading,
  }
}

export const useBiometricInquiryId = (): UseBiometricInquiryIdResponse => {
  const [createInquiryId, { data: inquiryIdData, isSuccess: isCreateInquiryIdSuccess, isError: isCreateInquiryIdError, isLoading: isCreateInquiryIdLoading }] =
    useCreateInquiryIdMutation()
  const biometricOptOut = useBiometricOptOut()

  useEffect(() => {
    if (isCreateInquiryIdSuccess || !isNullOrUndefined(inquiryIdData)) {
      return
    }

    createInquiryId({ channel: Channel.PORTAL, biometricOptOut, biometricOptOutEnabled: typeof biometricOptOut === 'boolean' })
  }, [createInquiryId, inquiryIdData, isCreateInquiryIdSuccess, biometricOptOut])

  return {
    sessionToken: inquiryIdData?.sessionToken,
    inquiryId: inquiryIdData?.inquiryId,
    isCreateInquiryIdError,
    isCreateInquiryIdLoading,
    isCreateInquiryIdSuccess,
  }
}

// CA persona flow need to skipBiometricCheck all the time
// UK onfido flow need to create check first time
export const useBiometricHardIdCheck = (applicantId: string = '', skipBiometricCheck: boolean = false): UseBiometricHardIdCheck => {
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const [status, setStatus] = useState(BiometricHardIdCheckHookStatus.INITIAL)
  const [pollingStatus, setPollingStatus] = useState<PollingStatus>(skipBiometricCheck ? PollingStatus.IDENTITIES : PollingStatus.CHECK)

  const biometricStatusPollingOption =
    pollingStatus === PollingStatus.CHECK
      ? {
          pollingInterval: BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL,
        }
      : { skip: true }

  const {
    isSuccess: isCheckSuccess,
    isError: isCheckError,
    isFetching,
  } = useCheckBiometricStatusQuery({ applicant_id: applicantId, channel: Channel.PORTAL }, biometricStatusPollingOption)

  const identitiesStatusPollingOption =
    pollingStatus === PollingStatus.IDENTITIES
      ? {
          pollingInterval: BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL,
        }
      : {}

  const { data: identityBiometricCheckData } = useGetIdentitiesQuery(undefined, identitiesStatusPollingOption)

  useEffect(() => {
    if (!isFetching && (isCheckSuccess || isCheckError)) {
      setPollingStatus(PollingStatus.IDENTITIES)
    }
  }, [isFetching, isCheckSuccess, isCheckError])

  useEffect(() => {
    if (isFetching) {
      return
    }
    if (
      identityBiometricCheckData?.identityCheckStatus === IdentityCheckStatus.VERIFIED ||
      identityBiometricCheckData?.identityCheckStatus === IdentityCheckStatus.FAILED
    ) {
      logEvent(TrackingEvent.IDENTITY_CHECK_STATUS_UPDATED, { status: identityBiometricCheckData?.identityCheckStatus })
      setStatus(
        identityBiometricCheckData?.identityCheckStatus === IdentityCheckStatus.VERIFIED
          ? BiometricHardIdCheckHookStatus.VERIFIED
          : BiometricHardIdCheckHookStatus.FAILED
      )
      setPollingStatus(PollingStatus.STOP)
    }
  }, [identityBiometricCheckData, isFetching])

  return {
    status,
  }
}
